/**
 * PartyAddSteps — P18 (team feedback fb 33, 2026-10-10).
 *
 * The one "add a party" flow shared by every Add-party modal (Agreement,
 * Account, Insurance Policy), so the three read and behave the same (§3h).
 * First built on the Agreement for #6 (2026-09-13); lifted here so the
 * Policy and Account modals stop being bare pickers.
 *
 * Steps: 'choose' → 'create' | 'attach'.
 *   - 'create' sends the user to /persons/new with return_to = the record's
 *     Parties tab and return_field=new_party_person. PersonDetailPage ferries
 *     the new person's id back on that URL; the host page re-enters Edit mode
 *     (useNewPartyPersonResume) and reopens its modal on 'attach' with the
 *     person pre-selected (useAddPartyModal → initialPersonId).
 *   - 'attach' is each modal's own staging form (role + role-specific fields).
 * The person only becomes Active once attached in a role and saved (C-321).
 */
import { Alert, Button, Card, Group, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconLink, IconUserPlus } from '@tabler/icons-react';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

export type PartyAddStep = 'choose' | 'create' | 'attach';

/** Query param PersonDetailPage uses to ferry a newly created person back. */
export const NEW_PARTY_PERSON_PARAM = 'new_party_person';

/** The new-person form URL that returns to `returnTo` with the new id. */
export function newPartyPersonUrl(returnTo: string): string {
  return `/persons/new?return_to=${encodeURIComponent(returnTo)}&return_field=${NEW_PARTY_PERSON_PARAM}`;
}

/** Modal step state. On every open it resets the form (dismiss = discard,
 *  §3g) and lands on 'attach' with the person pre-selected when resuming a
 *  create round-trip, otherwise on 'choose'. */
export function usePartyAddStep(
  opened: boolean,
  initialPersonId: string | null | undefined,
  resetForm: (personId: string) => void,
): [PartyAddStep, (step: PartyAddStep) => void] {
  const [step, setStep] = React.useState<PartyAddStep>('choose');
  React.useEffect(() => {
    if (opened) {
      resetForm(initialPersonId ?? '');
      setStep(initialPersonId ? 'attach' : 'choose');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialPersonId]);
  return [step, setStep];
}

/** Section-level modal state. A resumed person id (from the host page) opens
 *  the modal pre-selected; it is held locally so clearing the page's one-shot
 *  flag doesn't yank it while the modal is open. */
export function useAddPartyModal(
  resumePersonId?: string | null,
  onResumeHandled?: () => void,
) {
  const [opened, setOpened] = React.useState(false);
  const [initialPersonId, setInitialPersonId] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (resumePersonId) {
      setInitialPersonId(resumePersonId);
      setOpened(true);
      onResumeHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePersonId]);
  return {
    opened,
    initialPersonId,
    open: () => { setInitialPersonId(null); setOpened(true); },
    close: () => { setOpened(false); setInitialPersonId(null); },
  };
}

/** Page-level resume: when the URL carries ?new_party_person=<id>, hand the id
 *  to `onResume` (which re-enters Edit mode and passes it to the section) and
 *  strip the param so it fires once.
 *
 *  `ready` must be true only once the record has loaded. The detail pages fill
 *  their form from the record only while NOT in Edit mode, so entering Edit
 *  mode before the data arrives would leave the form blank. That happens when
 *  the user spends longer than the query cache keeps the record (5 min) on the
 *  new-person form. Waiting for `ready` lets the fill run first, in the same
 *  commit, with Edit mode still off. */
export function useNewPartyPersonResume(
  ready: boolean,
  onResume: (personId: string) => void,
): void {
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    if (!ready) return;
    const pid = searchParams.get(NEW_PARTY_PERSON_PARAM);
    if (pid) {
      onResume(pid);
      const next = new URLSearchParams(searchParams);
      next.delete(NEW_PARTY_PERSON_PARAM);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, searchParams, setSearchParams]);
}

interface ChooserProps {
  /** The record being added to, shown at the top for confirmation. */
  recordLabel: string;
  /** Lower-case noun for the record: 'agreement' | 'policy' | 'account'. */
  recordNoun: string;
  onPick: (step: 'create' | 'attach') => void;
  onCancel: () => void;
}

export function PartyAddChooser({ recordLabel, recordNoun, onPick, onCancel }: ChooserProps) {
  return (
    <Stack>
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm" fw={500}>{recordLabel}</Text>
        <Text size="xs" c="dimmed">
          Choose how you would like to add a party to this {recordNoun}.
        </Text>
      </Alert>

      <Card withBorder padding="md" radius="sm" onClick={() => onPick('create')}
        style={{ cursor: 'pointer' }}>
        <Stack gap={4}>
          <Group gap="xs"><IconUserPlus size={16} /><Text fw={600}>Create new person</Text></Group>
          <Text size="sm" c="dimmed">
            The person (or legal entity) does not exist yet. You will fill in
            their details on the new-person form, then return here to attach
            them to this {recordNoun} in a role.
          </Text>
        </Stack>
      </Card>

      <Card withBorder padding="md" radius="sm" onClick={() => onPick('attach')}
        style={{ cursor: 'pointer' }}>
        <Stack gap={4}>
          <Group gap="xs"><IconLink size={16} /><Text fw={600}>Attach existing person</Text></Group>
          <Text size="sm" c="dimmed">
            The person already exists in the system. Pick them and assign a
            role; the party row is staged and commits on Save Changes.
          </Text>
        </Stack>
      </Card>

      <Group justify="flex-end" pt="sm">
        <Button variant="default" onClick={onCancel}>Cancel</Button>
      </Group>
    </Stack>
  );
}

interface CreateStepProps {
  onBack: () => void;
  onOpenForm: () => void;
}

export function PartyCreatePersonStep({ onBack, onOpenForm }: CreateStepProps) {
  return (
    <Stack>
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        You will be taken to the new-person form. After you save the person,
        you will return here with them pre-selected — just choose their role
        and Stage the party (it commits, and the person becomes active, when
        you click Save Changes).
      </Alert>
      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>Back</Button>
        <Button leftSection={<IconUserPlus size={14} />} onClick={onOpenForm}>
          Open new-person form
        </Button>
      </Group>
    </Stack>
  );
}
